import { styled } from '@mui/material/styles';
import {
  Box,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Switch,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useClientAddressUse } from '../../../hooks/display/useClientAddressUse';
import { ClientAddress } from '../../../data/models/client.model';

const PREFIX = 'Address';

const classes = {
  title: `${PREFIX}-title`,
  addressLine: `${PREFIX}-addressLine`,
  actionsRow: `${PREFIX}-actionsRow`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 3,

  '& .MuiCardActions-root': {
    padding: '0 !important',
    '& .MuiCardActions-spacing': {
      padding: '0 !important',
    },
  },

  '& .MuiCardContent-root': {
    color: `${theme.palette.lightTextPrimary.main} !important`,
  },

  [`& .${classes.title}`]: {
    fontWeight: 600,
    fontSize: 18,
    lineHeight: '24px',
    letterSpacing: '0px',
  },

  [`& .${classes.addressLine}`]: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },

  [`& .${classes.actionsRow}`]: {
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.25),
    marginLeft: theme.spacing(1.375),
    marginBottom: theme.spacing(0.375),
  },
}));

interface AddressProps {
  clientAddress: ClientAddress;
  editAddress: (address: ClientAddress) => void;
  activateAddress: (address: ClientAddress) => void;
  deactivateAddress: (address: ClientAddress) => void;
  deleteAddress: (address: ClientAddress) => void;
  readonly: boolean;
}

export default function Address({
  clientAddress,
  editAddress,
  activateAddress,
  deactivateAddress,
  deleteAddress,
  readonly,
}: AddressProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (evt: any, address: ClientAddress) => {
    if (evt.target.checked) {
      activateAddress(address);
    } else {
      deactivateAddress(address);
    }
  };

  return (
    <StyledCard elevation={2}>
      <CardContent>
        <Box className={classes.title}>
          {useClientAddressUse(clientAddress.addressUseBC)}
        </Box>
        <div className={classes.addressLine}>{clientAddress.addressLine1}</div>
        {!!clientAddress.addressLine2 && (
          <Box className={classes.addressLine}>
            {clientAddress.addressLine2}
          </Box>
        )}
        <Box className={classes.addressLine}>
          {clientAddress.city}, {clientAddress.stateCode}{' '}
          {clientAddress.zipCode}
        </Box>
      </CardContent>
      {!readonly && !isSuperReaderUser && (
        <CardActions className={classes.actionsRow}>
          <Tooltip title={t('client.editAddress')}>
            <span>
              <IconButton
                data-testid="edit-address"
                onClick={(evt) => editAddress(clientAddress)}
                color="primary"
                size="small">
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('client.deleteAddress')}>
            <span>
              <IconButton
                data-testid="delete-address"
                onClick={(evt) => deleteAddress(clientAddress)}
                color="error"
                size="small"
                disabled={clientAddress.state === 'active'}>
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              clientAddress.state === 'active'
                ? t('client.active')
                : t('client.inactive')
            }>
            <Switch
              size="small"
              checked={clientAddress.state === 'active'}
              onChange={(evt) => handleActiveChange(evt, clientAddress)}
            />
          </Tooltip>
        </CardActions>
      )}
    </StyledCard>
  );
}
